import type { PublicApplySettings } from '../settings/publicSettingsClient';
import { normalizeFinancialAidAcademicYearLabel } from './financialAidAcademicYear';

export type PublicFinancialAidEntryClosedReason =
  | 'not_configured'
  | 'before_open'
  | 'after_cutoff';

export type PublicFinancialAidEntryState = {
  status: 'open' | 'closed';
  closedReason: PublicFinancialAidEntryClosedReason | null;
  schoolName: string;
  schoolShortName: string;
  logoUrl: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  schoolYear: string | null;
  openDate: string | null;
  onTimeDeadline: string | null;
  hardCutoffDeadline: string | null;
};

type PublicFinancialAidCycle = NonNullable<NonNullable<PublicApplySettings['financialAid']>['cycle']>;

const FINANCIAL_AID_ENTRY_PATHS = new Set([
  // Canonical parent routes — the URLs the school shares with families and
  // the paths invite/application links resolve to. Signed-out visitors must
  // land on the Financial Aid entry, never the generic sign-in page.
  '/parent/portal/financial-aid',
  '/parent/portal/financialaid',
  '/parent/portal--new/financial-aid',
  '/parent/portal--new/financialaid',
  '/school/parent/portal--new/financial-aid',
  '/school/parent/portal--new/financialaid',
]);

const CANONICAL_FINANCIAL_AID_ENTRY_PATH = '/parent/portal--new/financial-aid';

const readText = (value: unknown): string => String(value ?? '').trim();

const readOptionalText = (value: unknown): string | null => {
  const text = readText(value);
  return text.length > 0 ? text : null;
};

const normalizePublicAcademicYear = (value: unknown): string | null =>
  normalizeFinancialAidAcademicYearLabel(value);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const toMidnightMs = (value: unknown): number | null => {
  const text = readText(value);
  if (!text) return null;
  const parsed = Date.parse(`${text}T00:00:00Z`);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveLatestOpenOrUpcomingPublicCycle = (
  financialAid: PublicApplySettings['financialAid'] | null | undefined,
  nowMs: number,
): PublicFinancialAidCycle | null => {
  const cycles = listPublicFinancialAidCycles(financialAid);
  const byLatestOpenDate = (left: PublicFinancialAidCycle, right: PublicFinancialAidCycle): number => {
    const leftOpenDate = toMidnightMs(left.openDate) ?? 0;
    const rightOpenDate = toMidnightMs(right.openDate) ?? 0;
    return rightOpenDate - leftOpenDate
      || readText(right.schoolYear).localeCompare(readText(left.schoolYear));
  };
  const openCycles = cycles
    .filter((cycle) => {
      const openDateMs = toMidnightMs(cycle.openDate);
      const hardCutoffDeadlineMs = toMidnightMs(cycle.hardCutoffDeadline);
      return openDateMs != null
        && nowMs >= openDateMs
        && (hardCutoffDeadlineMs == null || nowMs < hardCutoffDeadlineMs);
    })
    .sort(byLatestOpenDate);
  if (openCycles[0]) {
    return openCycles[0];
  }

  const upcomingCycles = cycles
    .filter((cycle) => {
      const openDate = toMidnightMs(cycle.openDate);
      return openDate != null && openDate > nowMs;
    })
    .sort((left, right) => {
      const leftOpenDate = toMidnightMs(left.openDate) ?? Number.MAX_SAFE_INTEGER;
      const rightOpenDate = toMidnightMs(right.openDate) ?? Number.MAX_SAFE_INTEGER;
      return leftOpenDate - rightOpenDate
        || readText(left.schoolYear).localeCompare(readText(right.schoolYear));
    });
  if (upcomingCycles[0]) {
    return upcomingCycles[0];
  }

  return cycles.sort(byLatestOpenDate)[0] ?? null;
};

const normalizePath = (path: unknown): string => {
  const text = readText(path).replace(/\/+$/, '');
  return text || '/';
};

export const isPublicFinancialAidEntryPath = (path: unknown): boolean =>
  FINANCIAL_AID_ENTRY_PATHS.has(normalizePath(path));

export const resolvePublicFinancialAidEntryReturnTo = (
  pathname: unknown,
  search: unknown,
): string => {
  const path = normalizePath(pathname);
  const query = readText(search);
  if (!isPublicFinancialAidEntryPath(path)) {
    return `${path}${query.startsWith('?') ? query : query ? `?${query}` : ''}`;
  }
  return `${CANONICAL_FINANCIAL_AID_ENTRY_PATH}${query.startsWith('?') ? query : query ? `?${query}` : ''}`;
};

const resolveSchoolShortName = (
  shortName: unknown,
  schoolName: string,
): string => {
  const configuredShortName = readOptionalText(shortName);
  if (configuredShortName) return configuredShortName;
  if (schoolName === 'your school') return 'School';
  const initials = schoolName
    .split(/\s+/)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 4);
  return initials || 'School';
};

const readPublicFinancialAidCycle = (
  value: unknown,
  schoolYearOverride?: unknown,
): PublicFinancialAidCycle | null => {
  const record = asRecord(value);
  const schoolYear = normalizePublicAcademicYear(schoolYearOverride)
    ?? normalizePublicAcademicYear(record.schoolYear);
  if (!schoolYear) return null;

  return {
    schoolYear,
    openDate: readOptionalText(record.openDate),
    onTimeDeadline: readOptionalText(record.onTimeDeadline),
    hardCutoffDeadline: readOptionalText(record.hardCutoffDeadline),
    lateFeeAmount: Number.isFinite(Number(record.lateFeeAmount)) ? Number(record.lateFeeAmount) : null,
  };
};

const listPublicFinancialAidCycles = (
  financialAid: PublicApplySettings['financialAid'] | null | undefined,
): PublicFinancialAidCycle[] => {
  const cyclesByYear = new Map<string, PublicFinancialAidCycle>();
  const rawCycleMap = financialAid?.cycles;
  const cycleMap = typeof rawCycleMap === 'string'
    ? (() => {
        try {
          return JSON.parse(rawCycleMap);
        } catch {
          return null;
        }
      })()
    : rawCycleMap;

  if (Array.isArray(cycleMap)) {
    for (const rawCycle of cycleMap) {
      const cycle = readPublicFinancialAidCycle(rawCycle);
      if (cycle?.schoolYear) cyclesByYear.set(cycle.schoolYear, cycle);
    }
  } else {
    for (const [schoolYear, rawCycle] of Object.entries(asRecord(cycleMap))) {
      const cycle = readPublicFinancialAidCycle(rawCycle, schoolYear);
      if (cycle?.schoolYear) cyclesByYear.set(cycle.schoolYear, cycle);
    }
  }

  const primaryCycle = readPublicFinancialAidCycle(financialAid?.cycle);
  if (primaryCycle?.schoolYear && !cyclesByYear.has(primaryCycle.schoolYear)) {
    cyclesByYear.set(primaryCycle.schoolYear, primaryCycle);
  }

  return [...cyclesByYear.values()];
};

const resolvePublicFinancialAidCycle = (
  financialAid: PublicApplySettings['financialAid'] | null | undefined,
  requestedAcademicYear: unknown,
  nowMs: number,
): PublicFinancialAidCycle | null => {
  const hasRequestedYear = Boolean(readOptionalText(requestedAcademicYear));
  const requestedYear = normalizePublicAcademicYear(requestedAcademicYear);
  if (hasRequestedYear && !requestedYear) {
    return null;
  }
  if (requestedYear) {
    return listPublicFinancialAidCycles(financialAid)
      .find((cycle) => cycle.schoolYear === requestedYear) ?? null;
  }
  return resolveLatestOpenOrUpcomingPublicCycle(financialAid, nowMs);
};

export const resolvePublicFinancialAidEntryState = (
  settings: PublicApplySettings | null | undefined,
  nowMs: number = Date.now(),
  requestedAcademicYear?: unknown,
): PublicFinancialAidEntryState => {
  const organization = settings?.organization ?? null;
  const contactInfo = settings?.contactInfo ?? null;
  const cycle = resolvePublicFinancialAidCycle(settings?.financialAid, requestedAcademicYear, nowMs);
  const schoolName = readOptionalText(organization?.name) ?? 'your school';
  const schoolShortName = resolveSchoolShortName(organization?.shortName, schoolName);
  const schoolYear = readOptionalText(cycle?.schoolYear) ?? readOptionalText(requestedAcademicYear);
  const openDate = readOptionalText(cycle?.openDate);
  const onTimeDeadline = readOptionalText(cycle?.onTimeDeadline);
  const hardCutoffDeadline = readOptionalText(cycle?.hardCutoffDeadline);
  const openDateMs = toMidnightMs(openDate);
  const hardCutoffDeadlineMs = toMidnightMs(hardCutoffDeadline);
  const contactEmail =
    readOptionalText(organization?.emailFinancialAid)
    ?? readOptionalText(contactInfo?.email)
    ?? readOptionalText(organization?.emailAdmissions)
    ?? readOptionalText(organization?.emailSupport);
  const organizationPhone = readOptionalText(organization?.phone);
  const organizationPhoneExt = readOptionalText(organization?.phoneExt);
  const contactPhone =
    organizationPhone
      ? [organizationPhone, organizationPhoneExt].filter(Boolean).join(' ')
      : readOptionalText(contactInfo?.phone);
  const baseState = {
    schoolName,
    schoolShortName,
    logoUrl: readOptionalText(organization?.logo),
    contactEmail,
    contactPhone,
    schoolYear,
    openDate,
    onTimeDeadline,
    hardCutoffDeadline,
  };

  if (!schoolYear || openDateMs == null) {
    return {
      ...baseState,
      status: 'closed',
      closedReason: 'not_configured',
    };
  }

  if (nowMs < openDateMs) {
    return {
      ...baseState,
      status: 'closed',
      closedReason: 'before_open',
    };
  }

  if (hardCutoffDeadlineMs != null && nowMs >= hardCutoffDeadlineMs) {
    return {
      ...baseState,
      status: 'closed',
      closedReason: 'after_cutoff',
    };
  }

  return {
    ...baseState,
    status: 'open',
    closedReason: null,
  };
};
